import { createContext, ReactNode, useContext } from "react";

import { HttpError } from "~/utils/httpClient";

import {
  BusinessGetResponseType,
  BuyerRelationshipListResponseType,
  SupplierGetResponseType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";

import { useBuyerRelationshipList } from "../../../../hooks/api/v2/buyerRelationship/useBuyerRelationshipList";

import { useDraftOrders } from "../../../../hooks/api/v2/useDraftOrders";

import { CardBrand } from "@stripe/stripe-js";

import { CheckoutSelectedFulfillmentType } from "../..";

export type PaymentMethod = {
  brand: CardBrand;
  last4Digits: string;
  stripePaymentMethodId: string;
};

export type CheckoutConnection = NonNullable<
  BuyerRelationshipListResponseType[number]["Relationship"]["BuyerRelationshipDetails"]
>;

export type CheckoutContextApi = {
  isLoading: boolean;
  supplier: SupplierGetResponseType | undefined;
  supplierUrlIdInParam: string;
  connection: CheckoutConnection | undefined;
  storefront: SupplierStorefrontV2GetResponseType | undefined;
  business: BusinessGetResponseType | undefined;
  priceTierLink: string | undefined;
  error: HttpError<unknown> | null;
  paymentMethods: PaymentMethod[];
  storefrontFulfillmentType: CheckoutSelectedFulfillmentType; // This is the fulfillment type from the storefront itself.
  checkoutFulfillmentType: CheckoutSelectedFulfillmentType; // This is the fulfillment type from the checkout flow. It changes whenever the user changes their fulfillment type while at checkout.
  clearBasket: () => void;
  updateFulfillmentType: (type: CheckoutSelectedFulfillmentType) => void;
  deleteDraftOrder: ReturnType<typeof useDraftOrders>["deleteOrder"];
};

export type CheckoutProviderProps = {
  children: ReactNode;
};

export type UseConnectionForCheckoutProps = {
  supplierId: number;
};

export const validFulfillmentTypes: CheckoutSelectedFulfillmentType[] = [
  "direct",
  "collection",
  "haulier",
  "courier",
  "sellarDistribution",
];

export const CheckoutContext = createContext<CheckoutContextApi | null>(null);

export const useCheckout = () => {
  const context = useContext(CheckoutContext);

  if (!context) {
    throw new Error("useCheckout() must be used within <CheckoutProvider />");
  }

  return context;
};

export const useConnectionForCheckout = ({
  supplierId,
}: UseConnectionForCheckoutProps) => {
  const buyerRelationshipQuery = useBuyerRelationshipList(
    {
      query: [
        {
          type: "supplierId",
          modifier: "EQUALS",
          value: supplierId,
        },
      ],
      limit: 1,
    },
    {
      enabled: !!supplierId,
    },
  );

  const connection =
    buyerRelationshipQuery.data?.pages[0]?.[0]?.Relationship
      .BuyerRelationshipDetails;

  return {
    isLoading: buyerRelationshipQuery.isLoading,
    error: buyerRelationshipQuery.error,
    data: connection ?? undefined,
  };
};
